import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/App';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingBag, Tag, MapPin, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateCartQuantity, removeFromCart, user, login, clearCart } = useContext(UserContext);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [address, setAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [loading, setLoading] = useState(false);
  
  // Delivery fee state
  const [deliveryFee, setDeliveryFee] = useState(30);
  const [deliveryDistance, setDeliveryDistance] = useState(0);
  const [isDeliverable, setIsDeliverable] = useState(true);
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [loadingDeliveryFee, setLoadingDeliveryFee] = useState(false);
  
  // GST state
  const [gst, setGst] = useState(0);
  const [loadingGst, setLoadingGst] = useState(false);

  const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const grandTotal = totalAmount + (isDeliverable ? deliveryFee : 0) + gst - discount;

  // Fetch GST when cart total changes
  useEffect(() => {
    const fetchGst = async () => {
      if (totalAmount <= 0) {
        setGst(0);
        return;
      }
      
      setLoadingGst(true);
      try {
        const res = await axios.post(`${API}/calculate-gst`, { subtotal: totalAmount });
        setGst(res.data.gst_amount);
      } catch (error) {
        console.error('Failed to calculate GST:', error);
        // Fallback to client-side calculation
        setGst(Math.ceil(totalAmount / 100) * 5);
      } finally {
        setLoadingGst(false);
      }
    };
    
    fetchGst();
  }, [totalAmount]);

  // Fetch delivery fee when cart changes
  useEffect(() => {
    const calculateDeliveryFee = async () => {
      if (cart.length === 0) {
        setDeliveryFee(0);
        setDeliveryDistance(0);
        setIsDeliverable(true);
        return;
      }
      
      // Get user location from localStorage
      const userLat = localStorage.getItem('userLocationLat');
      const userLng = localStorage.getItem('userLocationLng');
      
      if (!userLat || !userLng) {
        // No location set, use default fee
        setDeliveryFee(30);
        setDeliveryDistance(0);
        setIsDeliverable(true);
        setDeliveryMessage('Set location for accurate delivery fee');
        return;
      }
      
      const vendorId = cart[0]?.vendor_id;
      if (!vendorId) {
        setDeliveryFee(30);
        return;
      }
      
      setLoadingDeliveryFee(true);
      try {
        const res = await axios.post(`${API}/delivery/calculate-fee`, {
          user_lat: parseFloat(userLat),
          user_lng: parseFloat(userLng),
          vendor_id: vendorId
        });
        
        setDeliveryFee(res.data.delivery_fee);
        setDeliveryDistance(res.data.distance_km);
        setIsDeliverable(res.data.deliverable);
        setDeliveryMessage(res.data.message);
      } catch (error) {
        console.error('Failed to calculate delivery fee:', error);
        setDeliveryFee(30);
        setIsDeliverable(true);
      } finally {
        setLoadingDeliveryFee(false);
      }
    };
    
    calculateDeliveryFee();
  }, [cart]);

  const applyCoupon = async () => {
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode, order_amount: totalAmount });
      if (res.data.valid) {
        setDiscount(res.data.discount_amount);
        toast.success(`Coupon applied! Saved ₹${res.data.discount_amount}`);
      } else {
        toast.error(res.data.message || 'Invalid coupon');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon');
    }
  };

  const handleCheckout = async () => {
    if (!user) { login(); return; }
    if (!isDeliverable) {
      toast.error('Restaurant is outside delivery radius (max 8 km)');
      return;
    }
    if (!address.trim()) { toast.error('Please enter delivery address'); return; }
    setLoading(true);
    try {
      const vendorId = cart[0]?.vendor_id || '';
      const orderData = {
        user_id: user?.id || 'guest',
        vendor_id: vendorId,
        items: cart,
        subtotal: totalAmount,
        discount_amount: discount,
        delivery_fee: deliveryFee,
        delivery_distance_km: deliveryDistance,
        tax_amount: gst,
        total_amount: grandTotal,
        delivery_address: { address: address },
        payment_method: paymentMethod,
        coupon_code: couponCode || null
      };
      const res = await axios.post(`${API}/orders`, orderData, { withCredentials: true });
      toast.success('Order placed successfully!');
      clearCart();
      navigate('/order-success', { state: { orderData: res.data } });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to place order');
        login();
      } else {
        toast.error('Failed to place order');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full"><ArrowLeft className="w-6 h-6" /></button>
            <h1 className="text-2xl font-bold">Your Cart</h1>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        {cart.length === 0 ? (
          <div className="text-center py-20"><ShoppingBag className="w-20 h-20 mx-auto text-gray-300 mb-4" /><h2 className="text-xl font-semibold text-gray-600 mb-2">Your cart is empty</h2><Button onClick={() => navigate('/')} className="btn-primary text-white px-8 py-3 rounded-full">Browse Vendors</Button></div>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {cart.map(item => (
                <div key={`${item.menu_item_id}-${JSON.stringify(item.customizations)}`} className="bg-white rounded-xl shadow-md p-4 flex gap-4">
                  <img src={item.image_url} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                  <div className="flex-1">
                    <h3 className="font-bold">{item.name}</h3>
                    <p className="text-sm text-gray-500">{item.vendor_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-bold text-blue-600">₹{item.price}</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => updateCartQuantity(item.menu_item_id, item.customizations, item.quantity - 1)} className="p-1 bg-blue-100 rounded-full"><Minus className="w-4 h-4 text-blue-600" /></button>
                        <span className="font-semibold w-8 text-center">{item.quantity}</span>
                        <button onClick={() => updateCartQuantity(item.menu_item_id, item.customizations, item.quantity + 1)} className="p-1 bg-blue-100 rounded-full"><Plus className="w-4 h-4 text-blue-600" /></button>
                        <button onClick={() => removeFromCart(item.menu_item_id, item.customizations)} className="p-1 hover:bg-red-50 rounded-full ml-2"><Trash2 className="w-5 h-5 text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
              <h2 className="font-bold mb-3 flex items-center gap-2"><Tag className="w-5 h-5" />Apply Coupon</h2>
              <div className="flex gap-2">
                <Input placeholder="Enter coupon code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)} className="flex-1" />
                <Button onClick={applyCoupon} className="btn-primary text-white px-6">Apply</Button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <h2 className="text-lg font-bold mb-4">Bill Details</h2>
              <div className="space-y-3">
                <div className="flex justify-between"><span>Item Total</span><span className="font-semibold">₹{totalAmount.toFixed(2)}</span></div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    Delivery Fee {deliveryDistance > 0 && <span className="text-xs text-gray-500">({deliveryDistance} km)</span>}
                  </span>
                  <span className="font-semibold">
                    {loadingDeliveryFee ? '...' : isDeliverable ? `₹${deliveryFee}` : '-'}
                  </span>
                </div>
                <div className="flex justify-between"><span>GST</span><span className="font-semibold">{loadingGst ? '...' : `₹${gst}`}</span></div>
                <p className="text-xs text-gray-400 mt-1">Free packaging charges + platform fee</p>
                {discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span className="font-semibold">-₹{discount.toFixed(2)}</span></div>}
                <div className="border-t pt-3 flex justify-between text-lg font-bold"><span>Grand Total</span><span className="text-green-600" data-testid="grand-total">₹{grandTotal.toFixed(2)}</span></div>
              </div>
              <p className="text-xs text-gray-500 mt-3 italic" data-testid="no-return-notice">Think twice before ordering — no return is available.</p>
              <p className="text-xs text-gray-500 mt-1">Estimated delivery time: 35–40 mins</p>
              {deliveryMessage && deliveryDistance > 0 && (
                <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {deliveryMessage}
                </p>
              )}
            </div>
            
            {/* Out of delivery radius warning */}
            {!isDeliverable && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-700">Out of Delivery Radius</p>
                  <p className="text-sm text-red-600">This restaurant is {deliveryDistance} km away. Maximum delivery radius is 8 km.</p>
                  <p className="text-sm text-red-600 mt-1">Please select a restaurant closer to your location.</p>
                </div>
              </div>
            )}
            
            {!showCheckout ? (
              <Button 
                onClick={() => setShowCheckout(true)} 
                disabled={!isDeliverable}
                className={`w-full py-4 rounded-xl text-lg font-semibold ${isDeliverable ? 'btn-primary text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                {isDeliverable ? 'Proceed to Checkout' : 'Cannot Deliver to This Location'}
              </Button>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
                <div><label className="block font-semibold mb-2">Delivery Address</label><textarea placeholder="Enter complete address" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full p-3 border rounded-lg min-h-24" required /><p className="text-gray-500 text-xs mt-1">Please enter an accurate delivery address. Orders placed with incorrect address details are not eligible for refunds.</p></div>
                <div><label className="block font-semibold mb-2">Payment Method</label><div className="space-y-2">
                  <button onClick={() => setPaymentMethod('upi')} className={`w-full p-4 border-2 rounded-xl text-left ${paymentMethod === 'upi' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>💳 UPI (Google Pay, PhonePe, Paytm)</button>
                  <button onClick={() => setPaymentMethod('cod')} className={`w-full p-4 border-2 rounded-xl text-left ${paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>💵 Pay on Delivery (UPI / Online Only)</button>
                </div></div>
                <Button onClick={handleCheckout} disabled={loading || !isDeliverable} className="w-full btn-primary text-white py-4 rounded-xl text-lg font-semibold">{loading ? 'Placing Order...' : `Place Order - ₹${grandTotal.toFixed(2)}`}</Button>
                <p className="text-gray-500 text-xs mt-2 text-center">Orders will be accepted starting 20 Feb 2026.</p>
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default CartPage;
